import { useMemo } from 'react'

import { keepPreviousData } from '@tanstack/react-query'
import {
  GetAIChatSessionQuery,
  GetAIChatSessionQueryVariables,
  SearchAIChatSessionsInput,
  SearchAIChatSessionsQuery,
  SearchAIChatSessionsQueryVariables,
} from 'types/graphql'

import { QueryKey, useQuery } from '../utils/queryClient'

const AIChatSession = gql`
  query GetAIChatSessionQuery($id: Int!) {
    aiChatSession(id: $id) {
      ...AIChatSessionFragment
    }
  }
`

export const useGetAIChatSessionQuery = (args: {
  variables: GetAIChatSessionQueryVariables
  enabled?: boolean
}) => {
  const { variables, enabled } = args
  const { data, ...rest } = useQuery<GetAIChatSessionQuery, GetAIChatSessionQueryVariables>({
    placeholderData: keepPreviousData,
    queryKey: [QueryKey.AIChatSession, variables.id],
    queryDocument: AIChatSession,
    variables,
    enabled,
  })
  const session = useMemo(() => data?.aiChatSession, [data])
  return { session, ...rest }
}

const SearchAIChatSessions = gql`
  query SearchAIChatSessionsQuery($input: SearchAIChatSessionsInput!) {
    searchAIChatSessions(input: $input) {
      count
      results {
        ...AIChatSessionSearchResultFragment
      }
    }
  }

  fragment AIChatSessionSearchResultFragment on AIChatSessionSearchResult {
    session {
      ...AIChatSessionFragmentForSessionTable
    }
    chat {
      ...AIChatFragmentForSessionTable
    }
  }

  fragment AIChatSessionFragmentForSessionTable on AIChatSession {
    id
    startedAt
    anyChatHasCustomAnswer
    source
    sourceUrl
    organization {
      displayName
    }
    user {
      ...UserDataFragment
    }
  }

  fragment AIChatFragmentForSessionTable on AIChat {
    question
  }
`

export const useSearchAIChatSessionsQuery = (input: SearchAIChatSessionsInput) => {
  const { data, ...rest } = useQuery<SearchAIChatSessionsQuery, SearchAIChatSessionsQueryVariables>(
    {
      queryKey: [QueryKey.AIChatSessionOverview, input],
      queryDocument: SearchAIChatSessions,
      variables: { input },
    }
  )
  const { results, count } = useMemo(() => {
    return {
      results: data?.searchAIChatSessions?.results || [],
      count: data?.searchAIChatSessions?.count || 0,
    }
  }, [data])
  return { results, count, ...rest }
}
